import {
  adminGroupRulesControllerList,
  documentSpecificRulesControllerCreate,
  documentSpecificRulesControllerList,
  documentSpecificRulesControllerRemove,
  documentSpecificRulesControllerUpdate,
  getDocumentSpecificRulesControllerListQueryKey,
} from '@app/api-client';
import type { DocumentDto, DocumentSpecificRuleDto } from '@app/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { fetchAdminUsers } from '../../admin/api.js';
import { KEY_SITE_PROGRESS_ROOT } from '../../progression/hooks/useProgression.js';

/** Clé dédiée (évite cache d’erreur si un ancien `limit` invalide avait été utilisé). */
const ADMIN_USERS_FOR_DOCUMENT_RIGHTS_KEY = ['admin-users', 'document-rights'] as const;

/** `paginationQuerySchema` impose `limit` ≤ 100. */
const ADMIN_USERS_PAGE = { page: 1, limit: 100 } as const;

function toLocalDatetimeValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalDatetimeValue(local: string): string {
  return new Date(local).toISOString();
}

interface Props {
  document: DocumentDto;
  onClose: () => void;
}

export function DocumentSpecificRulesModal({ document, onClose }: Props) {
  const qc = useQueryClient();
  const docId = document.id;
  const listKey = getDocumentSpecificRulesControllerListQueryKey(docId);

  const { data: rules = [], isLoading: loadingRules } = useQuery({
    queryKey: listKey,
    queryFn: () => documentSpecificRulesControllerList(docId),
    enabled: Boolean(docId),
  });

  const { data: usersPage } = useQuery({
    queryKey: ADMIN_USERS_FOR_DOCUMENT_RIGHTS_KEY,
    queryFn: () => fetchAdminUsers(ADMIN_USERS_PAGE),
  });
  const users = usersPage?.data ?? [];

  const { data: groups = [] } = useQuery({
    queryKey: ['admin-group-rules'],
    queryFn: () => adminGroupRulesControllerList(),
  });

  const [editing, setEditing] = useState<DocumentSpecificRuleDto | null>(null);
  const [assigneeKind, setAssigneeKind] = useState<'user' | 'group'>('user');
  const [userId, setUserId] = useState('');
  const [groupRuleId, setGroupRuleId] = useState('');
  const [dateStart, setDateStart] = useState(() => toLocalDatetimeValue(new Date().toISOString()));
  const [dateEnd, setDateEnd] = useState('');
  const [redaction, setRedaction] = useState(false);
  const [validation, setValidation] = useState(false);
  const [consultation, setConsultation] = useState(true);
  const [active, setActive] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) return;
    setAssigneeKind(editing.assigneeType);
    if (editing.assigneeType === 'user') {
      setUserId(editing.assigneeId);
      setGroupRuleId('');
    } else {
      setGroupRuleId(editing.assigneeId);
      setUserId('');
    }
    setDateStart(toLocalDatetimeValue(editing.dateStart));
    setDateEnd(editing.dateEnd ? toLocalDatetimeValue(editing.dateEnd) : '');
    setRedaction(editing.redaction);
    setValidation(editing.validation);
    setConsultation(editing.consultation);
    setActive(editing.active);
    setFormError(null);
  }, [editing]);

  const resetNewForm = () => {
    setEditing(null);
    setAssigneeKind('user');
    setUserId('');
    setGroupRuleId('');
    setDateStart(toLocalDatetimeValue(new Date().toISOString()));
    setDateEnd('');
    setRedaction(false);
    setValidation(false);
    setConsultation(true);
    setActive(true);
    setFormError(null);
  };

  const createMut = useMutation({
    mutationFn: () => {
      const dateStartIso = fromLocalDatetimeValue(dateStart);
      const dateEndIso = dateEnd.trim() ? fromLocalDatetimeValue(dateEnd) : '';
      const base = {
        dateStart: dateStartIso,
        dateEnd: dateEndIso,
        redaction,
        validation,
        consultation,
        active,
      };
      return documentSpecificRulesControllerCreate(
        docId,
        assigneeKind === 'user' ? { userId, ...base } : { groupRuleId, ...base },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listKey });
      void qc.invalidateQueries({ queryKey: KEY_SITE_PROGRESS_ROOT });
      resetNewForm();
    },
  });

  const updateMut = useMutation({
    mutationFn: () => {
      if (!editing) throw new Error('Pas de règle à modifier');
      return documentSpecificRulesControllerUpdate(docId, editing.id, {
        dateStart: fromLocalDatetimeValue(dateStart),
        dateEnd: dateEnd.trim() ? fromLocalDatetimeValue(dateEnd) : null,
        redaction,
        validation,
        consultation,
        active,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listKey });
      void qc.invalidateQueries({ queryKey: KEY_SITE_PROGRESS_ROOT });
      resetNewForm();
    },
  });

  const removeMut = useMutation({
    mutationFn: (ruleId: string) => documentSpecificRulesControllerRemove(docId, ruleId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listKey });
      void qc.invalidateQueries({ queryKey: KEY_SITE_PROGRESS_ROOT });
      resetNewForm();
    },
  });

  const submitCreate = () => {
    setFormError(null);
    if (assigneeKind === 'user' && !userId) {
      setFormError('Choisissez un utilisateur.');
      return;
    }
    if (assigneeKind === 'group' && !groupRuleId) {
      setFormError('Choisissez un groupe.');
      return;
    }
    createMut.mutate();
  };

  const submitUpdate = () => {
    setFormError(null);
    updateMut.mutate();
  };

  const pending = createMut.isPending || updateMut.isPending;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 p-4 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-slate-200">
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <p className="text-[0.65rem] font-bold uppercase tracking-[0.15em] text-emerald-700">
              Droits document
            </p>
            <h2 className="text-lg font-semibold text-slate-900">{document.name}</h2>
            <p className="mt-1 text-xs text-slate-500">
              Règles spécifiques : qui peut rédiger, valider ou consulter, et entre quelles dates.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loadingRules ? (
            <p className="text-sm text-slate-500">Chargement…</p>
          ) : (
            <ul className="mb-6 space-y-2">
              {rules.length === 0 ? (
                <li className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                  Aucune règle spécifique. Ajoutez-en une ci-dessous.
                </li>
              ) : (
                rules.map((r) => (
                  <li
                    key={r.id}
                    className={`flex flex-wrap items-center justify-between gap-2 rounded-xl border px-4 py-3 text-sm ${
                      editing?.id === r.id
                        ? 'border-emerald-300 bg-emerald-50/50'
                        : 'border-slate-200 bg-white'
                    }`}
                  >
                    <div>
                      <span className="font-medium text-slate-900">{r.assigneeLabel}</span>
                      <span className="ml-2 text-xs text-slate-500">
                        ({r.assigneeType === 'user' ? 'utilisateur' : 'groupe'})
                      </span>
                      <div className="mt-1 text-xs text-slate-600">
                        {new Date(r.dateStart).toLocaleString('fr-FR')}
                        {' → '}
                        {r.dateEnd ? new Date(r.dateEnd).toLocaleString('fr-FR') : '∞'}
                        {' · '}
                        {[
                          r.redaction && 'rédaction',
                          r.validation && 'validation',
                          r.consultation && 'consultation',
                        ]
                          .filter(Boolean)
                          .join(', ') || 'aucun droit'}
                        {!r.active && ' · inactif'}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setEditing(r)}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                      >
                        Modifier
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (
                            typeof window !== 'undefined' &&
                            window.confirm('Supprimer cette règle ?')
                          ) {
                            removeMut.mutate(r.id);
                          }
                        }}
                        className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-medium text-rose-800 hover:bg-rose-100"
                      >
                        Supprimer
                      </button>
                    </div>
                  </li>
                ))
              )}
            </ul>
          )}

          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
            <h3 className="text-sm font-semibold text-slate-800">
              {editing ? 'Modifier la règle' : 'Nouvelle règle'}
            </h3>
            {!editing && (
              <div className="mt-3 flex gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="assignee-kind"
                    checked={assigneeKind === 'user'}
                    onChange={() => setAssigneeKind('user')}
                  />
                  Utilisateur
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="assignee-kind"
                    checked={assigneeKind === 'group'}
                    onChange={() => setAssigneeKind('group')}
                  />
                  Groupe
                </label>
              </div>
            )}

            {!editing && assigneeKind === 'user' && (
              <label htmlFor="usr-rule" className="mt-3 block text-xs font-medium text-slate-600">
                Collaborateur
                <select
                  id="usr-rule"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                >
                  <option value="">—</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.alias} ({u.alias})
                    </option>
                  ))}
                </select>
              </label>
            )}

            {!editing && assigneeKind === 'group' && (
              <label htmlFor="grp-rule" className="mt-3 block text-xs font-medium text-slate-600">
                Groupe
                <select
                  id="grp-rule"
                  value={groupRuleId}
                  onChange={(e) => setGroupRuleId(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                >
                  <option value="">—</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {editing && (
              <p className="mt-3 text-xs text-slate-600">
                Destinataire : <strong>{editing.assigneeLabel}</strong> — pour changer de personne
                ou de groupe, supprimez la règle et recréez-la.
              </p>
            )}

            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label htmlFor="ds-start" className="block text-xs font-medium text-slate-600">
                Début
                <input
                  id="ds-start"
                  type="datetime-local"
                  value={dateStart}
                  onChange={(e) => setDateStart(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                />
              </label>
              <label htmlFor="ds-end" className="block text-xs font-medium text-slate-600">
                Fin (optionnel)
                <input
                  id="ds-end"
                  type="datetime-local"
                  value={dateEnd}
                  onChange={(e) => setDateEnd(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                />
              </label>
            </div>

            <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-700">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={redaction}
                  onChange={(e) => setRedaction(e.target.checked)}
                />
                Rédaction
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={validation}
                  onChange={(e) => setValidation(e.target.checked)}
                />
                Validation
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={consultation}
                  onChange={(e) => setConsultation(e.target.checked)}
                />
                Consultation
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />
                Active
              </label>
            </div>

            {(formError || createMut.error || updateMut.error) && (
              <p className="mt-2 text-sm text-rose-600">
                {formError ??
                  (createMut.error instanceof Error ? createMut.error.message : null) ??
                  (updateMut.error instanceof Error ? updateMut.error.message : null) ??
                  'Erreur'}
              </p>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              {editing ? (
                <>
                  <button
                    type="button"
                    disabled={pending}
                    onClick={submitUpdate}
                    className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {pending ? 'Enregistrement…' : 'Enregistrer les changements'}
                  </button>
                  <button
                    type="button"
                    onClick={resetNewForm}
                    className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700"
                  >
                    Annuler
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  disabled={pending}
                  onClick={submitCreate}
                  className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {pending ? 'Création…' : 'Ajouter la règle'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
